import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import FormModal from '../../components/Modal/FormModal';
import EntityList, { type EntityItem } from '../../components/admin/dataManagement/EntityList';
import {
  brandService,
  brandCategoryService,
  cityService,
  conditionService,
  districtService,
  listingComplaintProcessService,
  listingComplaintTypeService,
  notificationTypeService,
  policyService,
  productTypeService,
  secondaryCategoryService,
  specConstraintService,
  statusService,
  topCategoryService,
  warrantyService,
} from '../../api';
import { SpecValueType } from '../../api/types/product-type/SpecConstraintViewModel';
import type { SpecAllowedValueViewModel } from '../../api/types/product-type/SpecConstraintViewModel';
import type { CityViewModel } from '../../api/types/city';
import type { TopCategoryViewModel } from '../../api/types/top-category';
import type { SecondaryCategoryViewModel } from '../../api/types/secondary-category';
import type { ProductTypeViewModel } from '../../api/types/product-type';
import type { BrandViewModel } from '../../api/types/brand';
import type {
  SpecConstraintCreateModel,
  SpecConstraintUpdateModel,
} from '../../api/types/spec-constraint/SpecConstraintModels';

type EntityKey =
  | 'BrandCategory'
  | 'Brand'
  | 'City'
  | 'Condition'
  | 'District'
  | 'ListingComplaintProcess'
  | 'ListingComplaintType'
  | 'NotificationType'
  | 'Policy'
  | 'ProductType'
  | 'SecondaryCategory'
  | 'Status'
  | 'TopCategory'
  | 'Warranty'
  | 'SpecConstraint';

type EntityItemExtended = EntityItem & {
  text?: string | null;
  title?: string | null;
  message?: string | null;
  slug?: string | null;
  brandId?: number;
  productTypeId?: number;
  cityId?: number;
  topCategoryId?: number;
  secondaryCategoryId?: number;
  key?: string;
  displayName?: string;
  valueType?: SpecValueType;
  allowedValues?: SpecAllowedValueViewModel[] | null;
  productTypes?: ProductTypeViewModel[] | null;
  productTypeIds?: number[] | null;
  isDeleted?: boolean;
};

const ENTITY_LABELS: Record<EntityKey, string> = {
  BrandCategory: 'Marka Kategorisi',
  Brand: 'Marka',
  City: 'Şehir',
  Condition: 'Kondisyon',
  District: 'İlçe',
  ListingComplaintProcess: 'İlan Şikayet Süreci',
  ListingComplaintType: 'İlan Şikayet Tipi',
  NotificationType: 'Bildirim Tipi',
  Policy: 'Sözleşmeler',
  ProductType: 'Ürün Tipi',
  SecondaryCategory: 'Alt Kategori',
  Status: 'Durum',
  TopCategory: 'Üst Kategori',
  Warranty: 'Garanti',
  SpecConstraint: 'Özellik',
};

function getSpecValueTypeLabel(valueType: SpecValueType | number | string | undefined): string {
  if (valueType === SpecValueType.Number || valueType === 0 || valueType === 'Number') return 'Sayı';
  if (valueType === SpecValueType.String || valueType === 1 || valueType === 'String') return 'Metin';
  if (valueType === SpecValueType.Enum || valueType === 2 || valueType === 'Enum') return 'Seçenek listesi';
  return valueType != null ? String(valueType) : '';
}

function getSpecProductTypeLabel(item: EntityItemExtended): string {
  const names = item.productTypes
    ?.map((pt) => pt.name)
    .filter((name): name is string => name != null && name !== '');
  if (names && names.length > 0) return names.join(', ');
  return 'Atanmamış';
}

function getDisplayName(item: EntityItemExtended): string {
  if (item.displayName != null && item.displayName !== '' && item.key != null && item.key !== '') {
    return `${item.displayName} (${item.key}) — ${getSpecValueTypeLabel(item.valueType)} · ${getSpecProductTypeLabel(item)}`;
  }
  if (item.name != null && item.name !== '') return String(item.name);
  if ('text' in item && item.text != null && item.text !== '') return String(item.text);
  if ('title' in item && item.title != null && item.title !== '') return String(item.title);
  if ('brandId' in item && 'productTypeId' in item)
    return `Marka ${item.brandId} - Ürün tipi ${item.productTypeId}`;
  return `#${item.id}`;
}

const DataManagementPage: React.FC = () => {
  const [entityKey, setEntityKey] = useState<EntityKey>('Status');
  const [items, setItems] = useState<EntityItemExtended[]>([]);
  const [deletedItems, setDeletedItems] = useState<EntityItemExtended[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<EntityItemExtended | null>(null);
  const [formState, setFormState] = useState<{
    name?: string;
    text?: string;
    title?: string;
    message?: string;
    slug?: string;
    cityId?: number;
    topCategoryId?: number;
    secondaryCategoryId?: number;
    brandId?: number;
    productTypeId?: number;
    specKey?: string;
    specDisplayName?: string;
    valueType?: SpecValueType;
    allowedValues?: string[];
  }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [cities, setCities] = useState<CityViewModel[]>([]);
  const [topCategories, setTopCategories] = useState<TopCategoryViewModel[]>([]);
  const [secondaryCategories, setSecondaryCategories] = useState<SecondaryCategoryViewModel[]>([]);
  const [productTypes, setProductTypes] = useState<ProductTypeViewModel[]>([]);
  const [brands, setBrands] = useState<BrandViewModel[]>([]);

  useEffect(() => {
    cityService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }).then((r) => {
      if (r.isSuccess && r.data) setCities(r.data);
    });
    topCategoryService.getAll().then((r) => {
      if (r.isSuccess && r.data) setTopCategories(r.data);
    });
    secondaryCategoryService.getAll({}).then((r) => {
      if (r.isSuccess && r.data) setSecondaryCategories(r.data);
    });
    productTypeService.getAll({}).then((r) => {
      if (r.isSuccess && r.data) setProductTypes(r.data);
    });
    brandService.getAll({}).then((r) => {
      if (r.isSuccess && r.data) setBrands(r.data);
    });
  }, []);

  const parseEntityList = (data: unknown): EntityItemExtended[] => {
    const raw = data as EntityItemExtended[] | { items?: EntityItemExtended[] };
    return Array.isArray(raw) ? raw : (raw?.items ?? []);
  };

  const loadItems = useCallback(() => {
    setLoading(true);
    setListError(null);
    setDeletedItems([]);

    if (entityKey === 'SpecConstraint') {
      const listParams = { pagination: { pageSize: 500, pageNumber: 1 } };
      Promise.all([
        specConstraintService.getAll(listParams),
        specConstraintService.getAll({ ...listParams, isDeleted: true }),
      ])
        .then(([activeRes, deletedRes]) => {
          if (!activeRes.isSuccess || !activeRes.data) {
            setItems([]);
            if (!activeRes.isSuccess && activeRes.message) setListError(activeRes.message);
          } else {
            setItems(parseEntityList(activeRes.data));
          }
          if (deletedRes.isSuccess && deletedRes.data) {
            setDeletedItems(parseEntityList(deletedRes.data));
          } else {
            setDeletedItems([]);
          }
        })
        .finally(() => setLoading(false));
      return;
    }

    const promises: Record<Exclude<EntityKey, 'SpecConstraint'>, () => Promise<{ data?: unknown[] | null; isSuccess: boolean; message?: string | null }>> = {
      Brand: () => brandService.getAll({}),
      BrandCategory: () => brandCategoryService.getAll({}),
      City: () => cityService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }),
      Condition: () => conditionService.getAll(),
      District: () => districtService.getAll({}),
      ListingComplaintProcess: () => listingComplaintProcessService.getAll({}),
      ListingComplaintType: () => listingComplaintTypeService.getAll({}),
      NotificationType: () => notificationTypeService.getAll({}),
      Policy: () => policyService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }),
      ProductType: () => productTypeService.getAll({}),
      SecondaryCategory: () => secondaryCategoryService.getAll({}),
      Status: () => statusService.getAll({}),
      TopCategory: () => topCategoryService.getAll(),
      Warranty: () => warrantyService.getAll(),
    };
    promises[entityKey]()
      .then((res) => {
        if (!res.isSuccess || !res.data) {
          setItems([]);
          if (!res.isSuccess && res.message) setListError(res.message);
          return;
        }
        setItems(parseEntityList(res.data));
      })
      .finally(() => setLoading(false));
  }, [entityKey]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleAddClick = () => {
    setEditingItem(null);
    setFormState(
      entityKey === 'SpecConstraint'
        ? { valueType: SpecValueType.Number, allowedValues: [''] }
        : {}
    );
    setSubmitError(null);
    setShowForm(true);
  };

  const handleUpdateClick = (item: EntityItemExtended) => {
    setEditingItem(item);
    if (entityKey === 'SpecConstraint') {
      const enumValues =
        item.allowedValues && item.allowedValues.length > 0
          ? item.allowedValues.map((av) => av.value)
          : item.valueType === SpecValueType.Enum
            ? ['']
            : [];
      setFormState({
        specKey: item.key != null ? String(item.key) : '',
        specDisplayName: item.displayName != null ? String(item.displayName) : '',
        valueType: item.valueType ?? SpecValueType.Number,
        productTypeId: item.productTypeId ?? item.productTypeIds?.[0] ?? item.productTypes?.[0]?.id,
        allowedValues: enumValues,
      });
    } else {
      setFormState({
        name: item.name != null ? String(item.name) : '',
        text: 'text' in item && item.text != null ? String(item.text) : '',
        title: 'title' in item && item.title != null ? String(item.title) : '',
        message: 'message' in item && item.message != null ? String(item.message) : '',
        slug: 'slug' in item && item.slug != null ? String(item.slug) : '',
        cityId: 'cityId' in item ? item.cityId : undefined,
        topCategoryId: 'topCategoryId' in item ? item.topCategoryId : undefined,
        secondaryCategoryId: 'secondaryCategoryId' in item ? item.secondaryCategoryId : undefined,
        brandId: 'brandId' in item ? item.brandId : undefined,
        productTypeId: 'productTypeId' in item ? item.productTypeId : undefined,
      });
    }
    setSubmitError(null);
    setShowForm(true);
  };

  const buildPayload = (): Record<string, unknown> => {
    const key = entityKey;
    if (
      key === 'Brand' ||
      key === 'Status' ||
      key === 'Condition' ||
      key === 'Warranty' ||
      key === 'TopCategory' ||
      key === 'City' ||
      key === 'ListingComplaintType'
    ) {
      return { name: formState.name || undefined };
    }
    if (key === 'ListingComplaintProcess') {
      return { text: formState.text || undefined };
    }
    if (key === 'NotificationType') {
      return { title: formState.title || undefined, message: formState.message || undefined };
    }
    if (key === 'Policy') {
      return { name: formState.name || undefined, slug: formState.slug || undefined, text: formState.text || undefined };
    }
    if (key === 'District') {
      return { name: formState.name || undefined, cityId: formState.cityId ?? 0 };
    }
    if (key === 'SecondaryCategory') {
      return { name: formState.name || undefined, topCategoryId: formState.topCategoryId ?? 0 };
    }
    if (key === 'ProductType') {
      return { name: formState.name || undefined, secondaryCategoryId: formState.secondaryCategoryId ?? 0 };
    }
    if (key === 'BrandCategory') {
      return { brandId: formState.brandId ?? 0, productTypeId: formState.productTypeId ?? 0 };
    }
    if (key === 'SpecConstraint') {
      const valueType = formState.valueType ?? SpecValueType.Number;
      const allowedValues =
        valueType === SpecValueType.Enum
          ? (formState.allowedValues ?? []).map((v) => v.trim()).filter((v) => v !== '')
          : undefined;
      return {
        key: formState.specKey?.trim(),
        displayName: formState.specDisplayName?.trim(),
        valueType,
        productTypeId: formState.productTypeId ?? 0,
        allowedValues,
      };
    }
    return {};
  };

  const validateSpecForm = (): string | null => {
    if (entityKey !== 'SpecConstraint') return null;
    if (!formState.specKey?.trim()) return 'Anahtar gereklidir.';
    if (!formState.specDisplayName?.trim()) return 'Görünen ad gereklidir.';
    if (!formState.productTypeId) return 'Ürün tipi gereklidir.';
    if (formState.valueType == null) return 'Değer tipi gereklidir.';
    if (formState.valueType === SpecValueType.Enum) {
      const vals = (formState.allowedValues ?? []).map((v) => v.trim()).filter((v) => v !== '');
      if (vals.length === 0) return 'Seçenek listesi tipi için en az bir izin verilen değer gereklidir.';
    }
    return null;
  };

  const handleFormAccept = async () => {
    setSubmitError(null);
    const validationError = validateSpecForm();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    const payload = buildPayload();
    try {
      let res: { isSuccess: boolean; message?: string | null };
      if (editingItem) {
        const updateFns: Record<EntityKey, (id: number | string, p: unknown) => Promise<{ isSuccess: boolean; message?: string | null }>> = {
          Brand: (id, p) => brandService.update(id, p as { name?: string | null }),
          BrandCategory: (id, p) => brandCategoryService.update(id, p as { brandId?: number | null; productTypeId?: number | null }),
          City: (id, p) => cityService.update(id, p as { name?: string | null }),
          Condition: (id, p) => conditionService.update(id, p as { name?: string | null }),
          District: (id, p) => districtService.update(id, p as { name?: string | null; cityId?: number | null }),
          ListingComplaintProcess: (id, p) => listingComplaintProcessService.update(id, p as { text?: string | null }),
          ListingComplaintType: (id, p) => listingComplaintTypeService.update(id, p as { name?: string | null }),
          NotificationType: (id, p) => notificationTypeService.update(id, p as { title?: string | null; message?: string | null }),
          Policy: (id, p) => policyService.update(id, p as { name?: string | null; slug?: string | null; text?: string | null }),
          ProductType: (id, p) => productTypeService.update(id, p as { name?: string | null; secondaryCategoryId?: number | null }),
          SecondaryCategory: (id, p) => secondaryCategoryService.update(id, p as { name?: string | null; topCategoryId?: number | null }),
          Status: (id, p) => statusService.update(id, p as { name?: string | null }),
          TopCategory: (id, p) => topCategoryService.update(id, p as { name?: string | null }),
          Warranty: (id, p) => warrantyService.update(id, p as { name?: string | null }),
          SpecConstraint: (id, p) => specConstraintService.update(id, p as SpecConstraintUpdateModel),
        };
        res = await updateFns[entityKey](editingItem.id, payload);
      } else {
        const createFns: Record<EntityKey, (p: unknown) => Promise<{ isSuccess: boolean; message?: string | null }>> = {
          Brand: (p) => brandService.create(p as { name?: string | null }),
          BrandCategory: (p) => brandCategoryService.create(p as { brandId: number; productTypeId: number }),
          City: (p) => cityService.create(p as { name?: string | null }),
          Condition: (p) => conditionService.create(p as { name?: string | null }),
          District: (p) => districtService.create(p as { name?: string | null; cityId: number }),
          ListingComplaintProcess: (p) => listingComplaintProcessService.create(p as { text?: string | null }),
          ListingComplaintType: (p) => listingComplaintTypeService.create(p as { name?: string | null }),
          NotificationType: (p) => notificationTypeService.create(p as { title?: string | null; message?: string | null }),
          Policy: (p) => policyService.create(p as { name?: string | null; slug?: string | null; text?: string | null }),
          ProductType: (p) => productTypeService.create(p as { name?: string | null; secondaryCategoryId: number }),
          SecondaryCategory: (p) => secondaryCategoryService.create(p as { name?: string | null; topCategoryId: number }),
          Status: (p) => statusService.create(p as { name?: string | null }),
          TopCategory: (p) => topCategoryService.create(p as { name?: string | null }),
          Warranty: (p) => warrantyService.create(p as { name?: string | null }),
          SpecConstraint: (p) => specConstraintService.create(p as SpecConstraintCreateModel),
        };
        res = await createFns[entityKey](payload);
      }
      if (res.isSuccess) {
        setShowForm(false);
        loadItems();
      } else {
        setSubmitError(res.message || 'İşlem başarısız.');
      }
    } catch (err: unknown) {
      let message = 'İstek başarısız.';
      if (axios.isAxiosError(err)) {
        const body = err.response?.data as { message?: string | null } | undefined;
        if (body?.message) message = body.message;
      }
      setSubmitError(message);
    }
  };

  const handleDelete = async (item: EntityItemExtended) => {
    setListError(null);
    const deleteFns: Record<EntityKey, (id: number | string) => Promise<{ isSuccess: boolean; message?: string | null }>> = {
      Brand: (id) => brandService.delete(id),
      BrandCategory: (id) => brandCategoryService.delete(id),
      City: (id) => cityService.delete(id),
      Condition: (id) => conditionService.delete(id),
      District: (id) => districtService.delete(id),
      ListingComplaintProcess: (id) => listingComplaintProcessService.delete(id),
      ListingComplaintType: (id) => listingComplaintTypeService.delete(id),
      NotificationType: (id) => notificationTypeService.delete(id),
      Policy: (id) => policyService.delete(id),
      ProductType: (id) => productTypeService.delete(id),
      SecondaryCategory: (id) => secondaryCategoryService.delete(id),
      Status: (id) => statusService.delete(id),
      TopCategory: (id) => topCategoryService.delete(id),
      Warranty: (id) => warrantyService.delete(id),
      SpecConstraint: (id) => specConstraintService.delete(id),
    };
    const res = await deleteFns[entityKey](item.id);
    if (res.isSuccess) {
      loadItems();
    } else {
      setListError(res.message || 'Silme işlemi başarısız.');
    }
  };

  const renderFormFields = () => {
    const key = entityKey;
    const nameOnly =
      key === 'Brand' ||
      key === 'Status' ||
      key === 'Condition' ||
      key === 'Warranty' ||
      key === 'TopCategory' ||
      key === 'City' ||
      key === 'ListingComplaintType';
    if (nameOnly) {
      return (
        <div className="mb-3">
          <label className="form-label">Ad</label>
          <input
            type="text"
            className="form-control"
            value={formState.name ?? ''}
            onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
          />
        </div>
      );
    }
    if (key === 'ListingComplaintProcess') {
      return (
        <div className="mb-3">
          <label className="form-label">Metin</label>
          <input
            type="text"
            className="form-control"
            value={formState.text ?? ''}
            onChange={(e) => setFormState((s) => ({ ...s, text: e.target.value }))}
          />
        </div>
      );
    }
    if (key === 'Policy') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Ad</label>
            <input
              type="text"
              className="form-control"
              value={formState.name ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Slug</label>
            <input
              type="text"
              className="form-control"
              value={formState.slug ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, slug: e.target.value }))}
              placeholder="örn: terms-of-service, privacy-policy, listing-agreement"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Metin</label>
            <textarea
              className="form-control"
              rows={8}
              value={formState.text ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, text: e.target.value }))}
            />
          </div>
        </>
      );
    }
    if (key === 'NotificationType') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Başlık</label>
            <input
              type="text"
              className="form-control"
              value={formState.title ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, title: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Mesaj</label>
            <input
              type="text"
              className="form-control"
              value={formState.message ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, message: e.target.value }))}
            />
          </div>
        </>
      );
    }
    if (key === 'District') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Ad</label>
            <input
              type="text"
              className="form-control"
              value={formState.name ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Şehir</label>
            <select
              className="form-select"
              value={formState.cityId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, cityId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>{c.name ?? c.id}</option>
              ))}
            </select>
          </div>
        </>
      );
    }
    if (key === 'SecondaryCategory') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Ad</label>
            <input
              type="text"
              className="form-control"
              value={formState.name ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Üst Kategori</label>
            <select
              className="form-select"
              value={formState.topCategoryId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, topCategoryId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {topCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.name ?? c.id}</option>
              ))}
            </select>
          </div>
        </>
      );
    }
    if (key === 'ProductType') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Ad</label>
            <input
              type="text"
              className="form-control"
              value={formState.name ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Alt Kategori</label>
            <select
              className="form-select"
              value={formState.secondaryCategoryId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, secondaryCategoryId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {secondaryCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.name ?? c.id}</option>
              ))}
            </select>
          </div>
        </>
      );
    }
    if (key === 'BrandCategory') {
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Marka</label>
            <select
              className="form-select"
              value={formState.brandId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, brandId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>{b.name ?? b.id}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Ürün Tipi</label>
            <select
              className="form-select"
              value={formState.productTypeId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, productTypeId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {productTypes.map((p) => (
                <option key={p.id} value={p.id}>{p.name ?? p.id}</option>
              ))}
            </select>
          </div>
        </>
      );
    }
    if (key === 'SpecConstraint') {
      const allowedRows = formState.allowedValues ?? [''];
      return (
        <>
          <div className="mb-3">
            <label className="form-label">Ürün Tipi</label>
            <select
              className="form-select"
              value={formState.productTypeId ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, productTypeId: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">Seçin</option>
              {productTypes.map((p) => (
                <option key={p.id} value={p.id}>{p.name ?? p.id}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Anahtar</label>
            <input
              type="text"
              className="form-control"
              value={formState.specKey ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, specKey: e.target.value }))}
              placeholder="örn: ram, cpu, panel_type"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Görünen Ad</label>
            <input
              type="text"
              className="form-control"
              value={formState.specDisplayName ?? ''}
              onChange={(e) => setFormState((s) => ({ ...s, specDisplayName: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Değer Tipi</label>
            <select
              className="form-select"
              value={formState.valueType ?? SpecValueType.Number}
              onChange={(e) => {
                const nextType = Number(e.target.value) as SpecValueType;
                setFormState((s) => ({
                  ...s,
                  valueType: nextType,
                  allowedValues: nextType === SpecValueType.Enum ? (s.allowedValues?.length ? s.allowedValues : ['']) : [],
                }));
              }}
            >
              <option value={SpecValueType.Number}>Sayı</option>
              <option value={SpecValueType.String}>Metin</option>
              <option value={SpecValueType.Enum}>Seçenek listesi</option>
            </select>
          </div>
          {formState.valueType === SpecValueType.Enum && (
            <div className="mb-3">
              <label className="form-label">İzin Verilen Değerler</label>
              {allowedRows.map((val, idx) => (
                <div key={idx} className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    value={val}
                    onChange={(e) => {
                      const next = [...allowedRows];
                      next[idx] = e.target.value;
                      setFormState((s) => ({ ...s, allowedValues: next }));
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => {
                      const next = allowedRows.filter((_, i) => i !== idx);
                      setFormState((s) => ({ ...s, allowedValues: next.length > 0 ? next : [''] }));
                    }}
                    aria-label="Değeri kaldır"
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setFormState((s) => ({ ...s, allowedValues: [...(s.allowedValues ?? ['']), ''] }))}
              >
                <i className="fas fa-plus me-1" />
                Değer Ekle
              </button>
            </div>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">Veri Yönetimi</h1>
      </div>
      <div className="card">
        <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
          <select
            className="form-select form-select-sm w-auto"
            value={entityKey}
            onChange={(e) => setEntityKey(e.target.value as EntityKey)}
          >
            {(Object.keys(ENTITY_LABELS) as EntityKey[]).map((key) => (
              <option key={key} value={key}>
                {ENTITY_LABELS[key]}
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleAddClick}>
            <i className="fas fa-plus me-1" />
            Yeni Ekle
          </button>
        </div>
        <div className="card-body p-0">
          {listError && (
            <div className="alert alert-danger m-3 mb-0 py-2" role="alert">
              {listError}
            </div>
          )}
          {loading ? (
            <div className="d-flex align-items-center justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center text-muted py-5">Kayıt bulunamadı.</div>
          ) : (
            <EntityList
              items={items}
              getDisplayName={getDisplayName}
              onUpdate={handleUpdateClick}
              onDelete={handleDelete}
            />
          )}
        </div>
      </div>
      {entityKey === 'SpecConstraint' && !loading && deletedItems.length > 0 && (
        <div className="card mt-3">
          <div className="card-header">
            <h2 className="h6 mb-0">Silinmiş Özellikler</h2>
          </div>
          <div className="card-body p-0">
            <EntityList
              items={deletedItems}
              getDisplayName={getDisplayName}
              hideActions
            />
          </div>
        </div>
      )}
      <FormModal
        title={editingItem ? `${ENTITY_LABELS[entityKey]} Güncelle` : `Yeni ${ENTITY_LABELS[entityKey]}`}
        show={showForm}
        onClose={() => setShowForm(false)}
        onAccept={handleFormAccept}
        acceptLabel={editingItem ? 'Güncelle' : 'Ekle'}
      >
        {renderFormFields()}
        {submitError && (
          <div className="alert alert-danger py-2" role="alert">
            {submitError}
          </div>
        )}
      </FormModal>
    </>
  );
};

export default DataManagementPage;
